import React, { useState } from "react";
import type { FC } from "react";
import type { UnitGroup } from "@/types";
import { useFacilities } from "@/hooks/useFacilities";
import { scrollToMap, hasMapTab } from "@/utils/map/scrollToMap";
import {
    Tooltip,
    TooltipContent,
    TooltipProvider,
    TooltipTrigger,
} from "@/components/ui/tooltip";

interface AnnexLocationsMessageProps {
    unitGroups: UnitGroup[];
}

export const AnnexLocationsMessage: FC<AnnexLocationsMessageProps> = ({
    unitGroups,
}) => {
    const [tooltipOpen, setTooltipOpen] = useState(false);
    const mapTabExists = hasMapTab();
    const { facilities } = useFacilities();

    // Check if any displayed units are from annex facilities
    const hasAnnexUnits = React.useMemo(() => {
        if (!facilities || facilities.length === 0) {
            return false;
        }

        // Get unique facility IDs from displayed units
        const displayedFacilityIds = new Set(
            unitGroups.map(
                (unitGroup) => unitGroup.representative_unit.facility_id
            )
        );

        // Get all facility IDs to validate main_facility references
        const allFacilityIds = new Set(facilities.map((f) => f.id));

        // Check if any of these facilities are annexes (have a main_facility that exists in our facilities list)
        return facilities.some(
            (facility) =>
                displayedFacilityIds.has(facility.id) &&
                facility.main_facility != null &&
                allFacilityIds.has(facility.main_facility)
        );
    }, [unitGroups, facilities]);

    if (!hasAnnexUnits) {
        return null;
    }

    return (
        <p className="tw:font-bold">
            Some units located at nearby{" "}
            <TooltipProvider>
                <Tooltip
                    open={tooltipOpen}
                    onOpenChange={setTooltipOpen}
                >
                    <TooltipTrigger asChild>
                        <span
                            className="tw:underline"
                            onClick={() => setTooltipOpen(!tooltipOpen)}
                        >
                            annex locations
                        </span>
                    </TooltipTrigger>
                    <TooltipContent className="tw:max-w-xs tw:bg-white tw:shadow-md tw:text-black tw:text-sm">
                        <strong>Annex locations</strong> have separate controlled-access
                        entrances and are managed by our staff working
                        from an office at the main location.

                        {mapTabExists && (
                            <div className="tw:flex tw:justify-end tw:mt-2">
                                <button
                                    aria-label="Scroll to Map"
                                    className="locations-trigger btn btn-primary"
                                    onClick={() => {
                                        setTooltipOpen(false);
                                        scrollToMap();
                                    }}
                                >
                                    Show on map
                                </button>
                            </div>
                        )}
                    </TooltipContent>
                </Tooltip>
            </TooltipProvider>
            .
        </p>
    );
};

